import { IconButton, SelectField } from '../ui/controls.js';
import { Icon } from '../ui/icons.js';
import { activeReport, PREVIEW_SPEEDS, useParticlePreview, type PreviewReport } from './preview-store.js';

const fmt = (t: number) => (Math.round(t * 100) / 100).toFixed(2);

/**
 * Particle preview transport: play / pause, restart, stop, playback speed,
 * a playback-time scrubber (dragging it pauses and simulates to that time)
 * and the live particle count. `source` picks whose report is shown.
 */
export function PlaybackBar({ source = 'any', compact = false }: { source?: 'any' | 'scene'; compact?: boolean }) {
  const paused = useParticlePreview((s) => s.paused);
  const stopped = useParticlePreview((s) => s.stopped);
  const speed = useParticlePreview((s) => s.speed);
  const reports = useParticlePreview((s) => s.reports);
  const { togglePlay, restart, stop, seek, setSpeed } = useParticlePreview.getState();
  const report: PreviewReport | null = source === 'scene' ? (reports.scene ?? null) : activeReport(reports);
  const system = report?.systems[0];
  const time = report?.time ?? 0;
  const duration = system?.duration && system.duration > 0 ? system.duration : 5;
  // The scrubber covers one cycle, or more once the preview ran longer.
  const max = Math.max(duration, Math.ceil(time / duration) * duration);
  const playing = !paused && !stopped;

  return (
    <div className={`pe-playback${compact ? ' is-compact' : ''}`}>
      <IconButton icon={playing ? 'pause' : 'play'} title={playing ? 'Pause' : 'Play'} onClick={togglePlay} active={playing} />
      <IconButton icon="restart" title="Restart" onClick={restart} />
      <IconButton icon="stop" title="Stop (clears the particles)" onClick={stop} active={stopped} />
      <input
        className="pe-scrub"
        type="range"
        min={0}
        max={max}
        step={0.01}
        value={Math.min(time, max)}
        aria-label="Playback time"
        title="Playback time: drag to scrub (pauses the preview)"
        onChange={(e) => seek(Number(e.target.value))}
      />
      <span className="pe-time mono" title="Playback time (seconds)">
        {fmt(time)}s
      </span>
      <div className="pe-speed" title="Playback speed">
        <SelectField
          value={String(speed)}
          options={PREVIEW_SPEEDS.map((s) => ({ value: String(s), label: `${s}×` }))}
          onChange={(v) => setSpeed(Number(v))}
        />
      </div>
      {!compact && (
        <span className="pe-count mono" title="Live particles">
          <Icon name="particles" size={12} /> {report?.particles ?? 0}
        </span>
      )}
    </div>
  );
}
